import React, { useState } from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  DatePicker,
  DatePickerRangeType,
  Field,
  FieldProps,
  Form,
  Input,
  StandardFormSelect
} from "collections/Form";
import { boolean, date, InferType, mixed, number, object, string } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import {
  Ns_Ls_EntitlementIn,
  useCreateEntitlementMutation,
  useProductIdsQuery,
  useProductVersionsQuery
} from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import {
  CancelButton,
  DetailsLoader,
  OptionType,
  SubmitButton,
  ToggleSwitchInput
} from "elements";
import {
  AvailableEntitlementNameOptionTypes,
  AvailableProductOptionTypes,
  EntitlementAssignmentType,
  EntitlementAssignmentTypeOptionTypes,
  EntitlementTypeOptionTypes,
  EntitlementValueType,
  EntitlementValueTypeOptionTypes,
  LICENSE_MIN_BEGINS_DATE,
  LICENSE_MIN_EXPIRY_DATE,
  LICENSE_NO_BEGINS_DATE,
  LICENSE_NO_EXPIRY_DATE,
  LICENSE_UNLIMITED_COUNT,
  toLicenseBeginsIntSecondsString,
  toLicenseExpiryIntSecondsString
} from "utilities/licenseServerUtils";
import { omit } from "lodash";
import "./CreateEntitlementModal.scss";
import { NonNullableRequired } from "collections/Form/Form";
import { arrayUnique } from "utilities/arrayUtils";
import {
  getSupportedEntitlementConfig,
  SupportedEntitlementConfig,
  SupportedEntitlementNameOptionTypes
} from "../useEntitlements";
import { addDays } from "date-fns";

const NewEntitlementSchema = object().shape({
  name: string()
    .label(i18n.t("NAME"))
    .oneOf(AvailableEntitlementNameOptionTypes.map(entry => entry.value))
    .required(),
  productId: mixed()
    .label(i18n.t("TYPE"))
    .oneOf(AvailableProductOptionTypes.map(entry => entry.value))
    .notRequired(),
  productVersion: string()
    .label(i18n.t("VERSION_ALT"))
    .notRequired(),
  tenantId: string()
    .label(i18n.t("ENTITLEMENT_TENANT_ID"))
    .required(),
  activationCode: string()
    .label(i18n.t("ACTIVATION_KEY"))
    .notRequired(),
  mappingId1: string()
    .label(i18n.t("SKU"))
    .notRequired(),
  mappingId2: string()
    .label(i18n.t("ORDER_NUMBER"))
    .notRequired(),
  assignmentType: mixed()
    .label(i18n.t("ENTITLEMENT_ASSIGNMENT_TYPE"))
    .oneOf(EntitlementAssignmentTypeOptionTypes.map(entry => entry.value))
    .required(),
  type: mixed()
    .label(i18n.t("LICENSE_TYPE"))
    .oneOf(EntitlementTypeOptionTypes.map(entry => entry.value))
    .required(),
  valueType: string()
    .label(i18n.t("ENTITLEMENT_VALUE_TYPE"))
    .oneOf(EntitlementValueTypeOptionTypes.map(entry => entry.value))
    .required(),
  value: number()
    .label(i18n.t("VALUE"))
    .required(),
  valueIsUnlimitedBoolean: boolean()
    .label(i18n.t("UNLIMITED"))
    .required(),
  stringValue: string()
    .label(i18n.t("STRING_VALUE"))
    .notRequired(),
  begins: date()
    .label(i18n.t("START_DATE"))
    .min(LICENSE_MIN_BEGINS_DATE)
    .nullable()
    .notRequired(),
  expires: date()
    .label(i18n.t("EXPIRES"))
    .min(LICENSE_MIN_EXPIRY_DATE)
    .nullable()
    .notRequired()
});

type NewEntitlementFormProps = InferType<typeof NewEntitlementSchema>;

const ProductVersions: React.FC<{
  productId: string;
  label: string;
  tabIndex: number;
  disabled: boolean;
}> = ({ productId, label, tabIndex, disabled }) => {
  const {
    data: productIdsData,
    fetching: productIdsFetching
  } = useProductIdsQuery({
    requestPolicy: "cache-and-network"
  });

  const foundProductId =
    (productId &&
      productIdsData?.getProductIds?.productIds.find(
        productVersion => productVersion === productId
      )) ||
    "";

  const {
    data: productVersionData,
    fetching: productVersionsFetching
  } = useProductVersionsQuery({
    variables: {
      productId: foundProductId
    },
    requestPolicy: "cache-and-network",
    pause: !foundProductId
  });

  const productVersionsData = foundProductId
    ? productVersionData?.getProductVersions
    : undefined;

  const baseProductVersionSet: Array<string> = arrayUnique(
    productVersionsData?.versions.map(productVersion => {
      const arr = productVersion.split(".");
      return arr.length >= 2 ? `${arr[0]}.${arr[1]}` : productVersion;
    }) ?? []
  );

  const productVersionList: Array<OptionType> =
    baseProductVersionSet
      .map(productVersion => ({
        text: productVersion,
        value: productVersion
      }))
      .reverse() ?? [];

  const busy = productIdsFetching || productVersionsFetching;

  return (
    <StandardFormSelect
      name="productVersion"
      label={label}
      options={productVersionList}
      tabIndex={tabIndex}
      inline={false}
      search={true}
      customValue={true}
      selectOnInput={true}
      clearable={true}
      busy={busy}
      disabled={disabled}
    />
  );
};

export const CreateEntitlementModal: React.FC<{
  refresh: () => void;
  tenantId: string | undefined;
}> = ({ tenantId, refresh }) => {
  const { t } = useTranslation();
  const [, createEntitlement] = useCreateEntitlementMutation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();

  const [advanced, setAdvanced] = useState(false);
  const [selectedEntitlementConfig, setSelectedEntitlementConfig] = useState<
    SupportedEntitlementConfig
  >();

  // Cast dates as any so that nullable dates won't cause type check issues.  Why can't we allow a type
  // checked schema which allows for nullable or non-required fields? It would be nice if the type checking
  // was a little smarter especially since yup supports nullable and notRequired.
  const initialValues: NonNullableRequired<NewEntitlementFormProps> = {
    name: "",
    productId: "",
    productVersion: "",
    tenantId: tenantId || "",
    activationCode: "",
    mappingId1: "",
    mappingId2: "",
    assignmentType: "",
    type: "",
    valueType: "",
    value: 0,
    valueIsUnlimitedBoolean: false,
    stringValue: "",
    begins: null as any,
    expires: null as any
  };

  const onSubmit = async (values: NewEntitlementFormProps) => {
    const value =
      values.valueType !== EntitlementValueType.Boolean ||
      !values.valueIsUnlimitedBoolean
        ? values.value
        : LICENSE_UNLIMITED_COUNT;

    const entitlementIn: Partial<Ns_Ls_EntitlementIn> = {
      ...omit(values, [
        "assignmentType",
        "valueIsUnlimitedBoolean",
        "value",
        "begins",
        "expires"
      ]),
      assignedToTenant:
        values.assignmentType === EntitlementAssignmentType.Account,
      value,
      begins: toLicenseBeginsIntSecondsString(
        values.begins ?? LICENSE_NO_BEGINS_DATE
      ),
      expires: toLicenseExpiryIntSecondsString(
        values.expires ?? LICENSE_NO_EXPIRY_DATE
      )
    };

    const entitlementResponse = await createEntitlement({
      entitlement: entitlementIn
    });

    if (entitlementResponse.data?.createEntitlement?.recordId) {
      closeModal();
      refresh();

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_ADDED_ENTITLEMENT"),
        persistThroughNav: true
      });
    } else if (entitlementResponse.error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: t("CREATE_ENTITLEMENT_ERROR", {
          message: entitlementResponse.error.message
        })
      });
    }
  };

  const handleToggleAdvanced = (
    values: NewEntitlementFormProps,
    setValues: (values: NonNullableRequired<NewEntitlementFormProps>) => void
  ) => {
    // Make sure the current advanced mode settings are applicable when switching to non-advanced mode
    if (advanced) {
      const updateValues: Partial<NewEntitlementFormProps> = {};

      let modified = false;

      if (!getSupportedEntitlementConfig(values.name)) {
        // Reset selected entitlement name
        updateValues.name = "";
        modified = true;
      }

      // Add more checks here if necessary

      if (modified) {
        const newValues: NewEntitlementFormProps = {
          ...values,
          ...updateValues
        };

        // Update all the values at once
        setValues(newValues as NonNullableRequired<NewEntitlementFormProps>);
      }
    }

    setAdvanced(!advanced);
  };

  const handleEntitlementNameChange = (
    newName: string,
    values: NewEntitlementFormProps,
    setValues: (values: NonNullableRequired<NewEntitlementFormProps>) => void
  ) => {
    // this logic only has meaning for non-advanced mode
    if (advanced) {
      return;
    }

    const currentEntitlementConfig = getSupportedEntitlementConfig(newName);

    if (!currentEntitlementConfig) {
      return;
    }

    setSelectedEntitlementConfig(currentEntitlementConfig);

    // Be sure to manually include the new entitlement name since the current
    // value has not been applied by formik during this callback
    const newValues: NewEntitlementFormProps = {
      ...values,
      name: newName
    };

    newValues.productId = currentEntitlementConfig.productType;
    newValues.type = currentEntitlementConfig.licenseType;
    newValues.assignmentType = currentEntitlementConfig.assignmentType;
    newValues.valueType = currentEntitlementConfig.valueType;
    if (currentEntitlementConfig.valueType === EntitlementValueType.Boolean) {
      newValues.value = 0;
    }

    newValues.valueIsUnlimitedBoolean =
      currentEntitlementConfig.valueTypeUnlimited;

    // default the start ane expire date to today and 30 days from now
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (
      currentEntitlementConfig.startDateEnabled &&
      currentEntitlementConfig.startDateRequired
    ) {
      newValues.begins = today;
    }

    if (
      currentEntitlementConfig.expiresEnabled &&
      currentEntitlementConfig.expiresRequired
    ) {
      const thirtyDaysFromNow = addDays(today, 30);
      thirtyDaysFromNow.setHours(23, 59, 59, 0);
      newValues.expires = thirtyDaysFromNow;
    }

    // Update all the values at once but wait until the select component has settled (wait until next event cycle)
    setTimeout(() => {
      setValues(newValues as NonNullableRequired<NewEntitlementFormProps>);
    });
  };

  return (
    <ModalForm
      title={t("ADD_ENTITLEMENT")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"CreateEntitlementModal"}
      validationSchema={NewEntitlementSchema}
      className="create-entitlement-modal fixed-lg full-max-height"
      closeIcon={true}
      fluid={false}
      enableReinitialize={true}
    >
      {({ values, setValues, isSubmitting, setFieldValue }) => {
        if (
          values.valueType === EntitlementValueType.Boolean &&
          values.valueIsUnlimitedBoolean &&
          values.value !== 0
        ) {
          setFieldValue("value", 0);
        }

        return (
          <Form>
            <DetailsLoader
              loading={isSubmitting}
              title={t("SAVING_ENTITLEMENT")}
            >
              <div className="form-content">
                <div className="grid">
                  <div className="col-1 grid">
                    <label className="col-12" htmlFor="advancedMode">
                      {t("ADVANCED")}:
                    </label>
                    <div className="col-12 ml-1">
                      <ToggleSwitchInput
                        name="advancedMode"
                        checked={advanced}
                        onChange={() => handleToggleAdvanced(values, setValues)}
                        label={t("ADVANCED")}
                      />
                    </div>
                  </div>
                </div>
                <div className="grid ml-1">
                  <div className="col-6 mr-1">
                    <StandardFormSelect
                      name="name"
                      label={t("NAME")}
                      options={
                        advanced
                          ? AvailableEntitlementNameOptionTypes
                          : SupportedEntitlementNameOptionTypes
                      }
                      tabIndex={1}
                      inline={false}
                      onSelectionChange={newName =>
                        handleEntitlementNameChange(newName, values, setValues)
                      }
                    />
                  </div>

                  <div className="col-6">
                    <DatePicker
                      name="begins"
                      otherName="expires"
                      label={
                        !advanced &&
                        (!selectedEntitlementConfig ||
                          selectedEntitlementConfig?.startDateRequired)
                          ? t("START_DATE")
                          : t("START_DATE_OPTIONAL")
                      }
                      tabIndex={9}
                      rangeType={DatePickerRangeType.START}
                      minDate={LICENSE_MIN_BEGINS_DATE}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.startDateEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75 mr-1">
                    <StandardFormSelect
                      name="productId"
                      label={t("PRODUCT_TYPE_OPTIONAL")}
                      options={AvailableProductOptionTypes}
                      tabIndex={2}
                      inline={false}
                      clearable={true}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.productTypeEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75">
                    <DatePicker
                      name="expires"
                      otherName="begins"
                      label={
                        !advanced &&
                        (!selectedEntitlementConfig ||
                          selectedEntitlementConfig?.expiresRequired)
                          ? t("EXPIRES")
                          : t("EXPIRES_OPTIONAL")
                      }
                      tabIndex={10}
                      rangeType={DatePickerRangeType.END}
                      minDate={LICENSE_MIN_EXPIRY_DATE}
                      disabled={
                        !advanced && !selectedEntitlementConfig?.expiresEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75 mr-1">
                    <ProductVersions
                      productId={values.productId}
                      label={
                        !(
                          !advanced &&
                          !selectedEntitlementConfig?.productVersionEnabled
                        )
                          ? t("PRODUCT_VERSION_OPTIONAL")
                          : t("PRODUCT_VERSION_NOT_USED")
                      }
                      tabIndex={3}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.productVersionEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75">
                    <Input
                      className="tall"
                      name="activationCode"
                      label={t("ACTIVATION_KEY_OPTIONAL")}
                      tabIndex={11}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.activationKeyEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75 mr-1">
                    <StandardFormSelect
                      name="type"
                      label={t("LICENSE_TYPE")}
                      options={EntitlementTypeOptionTypes}
                      tabIndex={4}
                      inline={false}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.licenseTypeEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75">
                    <Input
                      className="tall"
                      name="mappingId1"
                      label={t("SKU_OPTIONAL")}
                      tabIndex={12}
                      disabled={
                        !advanced && !selectedEntitlementConfig?.skuEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75 mr-1">
                    <StandardFormSelect
                      name="assignmentType"
                      label={t("ENTITLEMENT_ASSIGNMENT_TYPE")}
                      options={EntitlementAssignmentTypeOptionTypes}
                      tabIndex={5}
                      inline={false}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.assignmentTypeEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75">
                    <Input
                      className="tall"
                      name="mappingId2"
                      label={t("ORDER_NUMBER_OPTIONAL")}
                      tabIndex={13}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.orderNumberEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75 mr-1">
                    <StandardFormSelect
                      name="valueType"
                      label={t("ENTITLEMENT_VALUE_TYPE")}
                      options={EntitlementValueTypeOptionTypes}
                      tabIndex={6}
                      inline={false}
                      disabled={
                        !advanced &&
                        !selectedEntitlementConfig?.valueTypeEnabled
                      }
                    />
                  </div>

                  <div className="col-6 mt-75">
                    <Input
                      className="tall"
                      name="stringValue"
                      label={
                        !advanced &&
                        (!selectedEntitlementConfig ||
                          !selectedEntitlementConfig?.stringEnabled)
                          ? t("STRING_VALUE_NOT_USED")
                          : t("STRING_VALUE_OPTIONAL")
                      }
                      tabIndex={14}
                      disabled={
                        !advanced && !selectedEntitlementConfig?.stringEnabled
                      }
                    />
                  </div>

                  {values.valueType === EntitlementValueType.Boolean ? (
                    <div className="col-6 mt-75 mr-1 grid">
                      <div className="col-10">
                        <Input
                          className="tall"
                          name="value"
                          type="number"
                          label={t("VALUE")}
                          tabIndex={7}
                          disabled={
                            values.valueIsUnlimitedBoolean ||
                            (!advanced &&
                              !selectedEntitlementConfig?.valueEnabled)
                          }
                        />
                      </div>
                      <div className="col-2 ph-0">
                        <Input
                          className="tall"
                          name="valueIsUnlimitedBoolean"
                          type="checkbox"
                          label={t("UNLIMITED")}
                          tabIndex={8}
                          disabled={
                            !advanced &&
                            !selectedEntitlementConfig?.valueTypeUnlimitedEnabled
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="col-6 mt-75 mr-1">
                      <Input
                        className="tall"
                        name="value"
                        type="number"
                        label={t("VALUE")}
                        tabIndex={7}
                        disabled={
                          !advanced && !selectedEntitlementConfig?.valueEnabled
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </DetailsLoader>

            <div className="btn-container">
              <CancelButton onClick={closeModal} />
              <Field name="submit">
                {({ form: { dirty, isValid } }: FieldProps) => (
                  <SubmitButton
                    testId="save"
                    disabled={isSubmitting || !isValid || !dirty}
                    isSubmitting={isSubmitting}
                    labelKey="ADD_ENTITLEMENT"
                  />
                )}
              </Field>
            </div>
          </Form>
        );
      }}
    </ModalForm>
  );
};
